import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, Check, Trash2, MessageSquare, Wrench, X, AlertTriangle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const notificationIcons = {
  mention: MessageSquare,
  task_assigned: Wrench,
  follow_up_warning: AlertTriangle,
  follow_up_urgent: AlertTriangle,
  follow_up_escalation: AlertTriangle,
  follow_up_neglected: AlertTriangle,
  delivery_reminder: Info,
  measurement_reminder: Info,
  inactive_project: Info,
};

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const dropdownRef = useRef(null);
  const prevUnreadCount = useRef(0);
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/notifications/unread-count`);
      const newCount = response.data.count;
      prevUnreadCount.current = newCount;
      setUnreadCount(newCount);
      setInitialCheckDone(true);
    } catch (error) {
      console.error('Error fetching unread count');
    }
  }, []);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/notifications?limit=20`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await axios.patch(`${API_URL}/notifications/${notification.id}/read`);
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read');
      }
    }
    
    // Navigate to link
    if (notification.link) {
      navigate(notification.link);
      setIsOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.patch(`${API_URL}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Mark all read error');
    }
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      const wasUnread = notifications.find(n => n.id === notificationId && !n.is_read);
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Delete notification error');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'teraz';
    if (diffMins < 60) return `${diffMins} min temu`;
    if (diffHours < 24) return `${diffHours} godz. temu`;
    if (diffDays < 7) return `${diffDays} dni temu`;
    return date.toLocaleDateString('pl-PL');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        data-testid="notification-bell"
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-[#FF6B35]' : 'text-[var(--crm-text-secondary)]'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF6B35] text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Desktop Dropdown / Mobile Full-screen Overlay */}
      {isOpen && (
        <>
          {/* Mobile backdrop */}
          <div className="fixed inset-0 bg-black/60 z-[60] md:hidden" onClick={() => setIsOpen(false)} />

          {/* Panel */}
          <div
            data-testid="notification-panel"
            className={
              // Mobile: full-screen overlay
              'fixed inset-0 z-[70] bg-[var(--crm-bg-main)] flex flex-col ' +
              // Desktop: fixed dropdown positioned near the bell
              'md:inset-auto md:fixed md:top-14 md:left-[16px] md:w-[400px] md:rounded-lg md:border md:border-[var(--crm-border)] md:shadow-2xl md:max-h-[520px] md:bg-[var(--crm-bg-dialog)]'
            }
          >
            {/* Sticky Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--crm-border)] bg-[var(--crm-bg-panel)] md:bg-[var(--crm-bg-dialog)] md:rounded-t-lg flex-shrink-0">
              <h3 className="text-[var(--crm-text-primary)] font-semibold text-base">Powiadomienia</h3>
              <div className="flex items-center gap-3">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    data-testid="mark-all-read-btn"
                    className="text-xs text-[#FF6B35] hover:text-[#E85D2E] flex items-center gap-1 font-medium"
                  >
                    <Check className="w-3.5 h-3.5" />
                    Oznacz wszystkie
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  data-testid="close-notifications-btn"
                  className="w-8 h-8 flex items-center justify-center rounded-full text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-[var(--crm-text-secondary)]">Ładowanie...</div>
              ) : notifications.length === 0 ? (
                <div className="p-12 text-center">
                  <Bell className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-[var(--crm-text-secondary)]">Brak powiadomień</p>
                </div>
              ) : (
                <>
                {notifications.map((notification) => {
                  const Icon = notificationIcons[notification.type] || Bell;
                  const isAlert = notification.type?.includes('follow_up') || notification.type?.includes('escalation');
                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      data-testid={`notification-${notification.id}`}
                      className={`px-4 py-3 border-b border-[var(--crm-border)]/60 cursor-pointer hover:bg-[var(--crm-bg-card-hover)] active:bg-white/10 transition-colors group ${
                        !notification.is_read ? 'bg-[#FF6B35]/[0.07]' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        {/* Icon */}
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isAlert
                            ? 'bg-amber-500/20 text-amber-400'
                            : !notification.is_read
                              ? 'bg-[#FF6B35]/20 text-[#FF6B35]'
                              : 'bg-[var(--crm-bg-btn-secondary)]/60 text-[var(--crm-text-secondary)]'
                        }`}>
                          <Icon className="w-4 h-4" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <p className={`text-sm font-semibold leading-snug line-clamp-2 ${
                              !notification.is_read ? 'text-[var(--crm-text-primary)]' : 'text-[var(--crm-text-secondary)]'
                            }`}>
                              {notification.title}
                            </p>
                            {/* Unread dot */}
                            {!notification.is_read && (
                              <span className="w-2.5 h-2.5 bg-[#FF6B35] rounded-full flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                          <p className="text-xs text-[var(--crm-text-secondary)] mt-1 leading-relaxed line-clamp-3">
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                              {notification.project_name && (
                                <span className="text-xs text-[#FF6B35] font-medium truncate max-w-[180px]">
                                  {notification.project_name}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-[11px] text-[var(--crm-text-muted)] whitespace-nowrap">
                                {formatTime(notification.created_at)}
                              </span>
                              <button
                                onClick={(e) => handleDeleteNotification(e, notification.id)}
                                className="opacity-0 group-hover:opacity-100 text-[var(--crm-text-muted)] hover:text-red-400 transition-opacity p-1"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
                }
                {/* See all link */}
                <button
                  onClick={() => { navigate('/powiadomienia'); setIsOpen(false); }}
                  data-testid="see-all-notifications-btn"
                  className="w-full py-3 text-center text-sm text-[#FF6B35] hover:text-[var(--crm-accent-hover)] font-medium hover:bg-[var(--crm-bg-card-hover)] transition-colors"
                >
                  Zobacz wszystkie powiadomienia
                </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
